import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";

export const STATUS_STYLES = {
  DRAFT: "bg-slate-100 text-slate-600 border-slate-200", PENDING_APPROVAL: "bg-purple-50 text-purple-700 border-purple-200",
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200", SUSPENDED: "bg-amber-50 text-amber-800 border-amber-200",
  RETIRED: "bg-slate-200 text-slate-500 border-slate-300", ARCHIVED: "bg-slate-200 text-slate-500 border-slate-300",
};
export const EFF_STYLES = {
  EFFECTIVE: "bg-emerald-600 text-white border-emerald-600", NEEDS_IMPROVEMENT: "bg-amber-400 text-[#003366] border-amber-400",
  INEFFECTIVE: "bg-red-600 text-white border-red-600", NOT_TESTED: "bg-white text-slate-500 border-slate-300 border-dashed",
  NOT_APPLICABLE: "bg-slate-100 text-slate-500 border-slate-200",
};
export const CRIT_STYLES = {
  CRITICAL: "bg-red-50 text-red-700 border-red-200", HIGH: "bg-orange-50 text-orange-700 border-orange-200",
  MEDIUM: "bg-blue-50 text-[#008080] border-blue-200", LOW: "bg-slate-50 text-slate-600 border-slate-200",
};
export const EXEC_STYLES = {
  SCHEDULED: "bg-slate-50 text-slate-600 border-slate-200", OPEN: "bg-blue-50 text-[#008080] border-blue-200",
  IN_PROGRESS: "bg-indigo-50 text-indigo-700 border-indigo-200", AWAITING_EVIDENCE: "bg-amber-50 text-amber-800 border-amber-200",
  AWAITING_REVIEW: "bg-purple-50 text-purple-700 border-purple-200", COMPLETED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  OVERDUE: "bg-red-600 text-white border-red-600", CANCELLED: "bg-slate-100 text-slate-400 border-slate-200",
};
export const EVID_STYLES = {
  SUBMITTED: "bg-blue-50 text-[#008080] border-blue-200", ACCEPTED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECTED: "bg-red-50 text-red-700 border-red-200", EXPIRED: "bg-amber-50 text-amber-800 border-amber-200",
  SUPERSEDED: "bg-slate-100 text-slate-500 border-slate-200", MISSING: "bg-red-50 text-red-700 border-red-200",
  COMPLETE: "bg-emerald-50 text-emerald-700 border-emerald-200", NONE_REQUIRED: "bg-slate-50 text-slate-500 border-slate-200",
};
export const DEF_STYLES = {
  OPEN: "bg-red-50 text-red-700 border-red-200", IN_REVIEW: "bg-blue-50 text-[#008080] border-blue-200",
  TREATMENT_SELECTED: "bg-purple-50 text-purple-700 border-purple-200", REMEDIATION_IN_PROGRESS: "bg-indigo-50 text-indigo-700 border-indigo-200",
  AWAITING_VALIDATION: "bg-amber-50 text-amber-800 border-amber-200", CLOSED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CANCELLED: "bg-slate-100 text-slate-400 border-slate-200",
};
export const lbl = (s) => (s ? String(s).replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : "-");
export const opts = (obj) => Object.entries(obj || {});

let cache = null;
export const useControlMeta = () => {
  const [meta, setMeta] = useState(cache);
  const reload = useCallback(() => api.get("/controls/meta").then(({ data }) => { cache = data; setMeta(data); }).catch(() => {}), []);
  useEffect(() => { if (!cache) reload(); }, [reload]);
  return meta;
};
export const resetControlMeta = () => { cache = null; };
export const hasPerm = (list, p) => (list || []).includes(p);

export const run = async (fn, ok) => {
  try { const r = await fn(); if (ok) toast.success(ok); return r; } catch (e) {
    const d = e?.response?.data?.detail;
    toast.error(typeof d === "object" && d?.message ? d.message : apiError(e, "Action failed"));
    return null;
  }
};

export const exportReport = async (report_type, format, extra = {}) => {
  const res = await api.post("/control-reports/export", { report_type, format, ...extra }, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `${report_type}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
  return res.headers["x-content-sha256"];
};

export const downloadControlEvidence = async (linkId) => {
  const { data } = await api.post(`/control-evidence/${linkId}/signed-url`);
  const res = await api.get(data.url.replace(/^\/api/, ""), { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "evidence";
  a.click();
  URL.revokeObjectURL(url);
};
